// PopulateTemplateModal.js
// ─────────────────────────────────────────────────────────────────────────────
// "Populate template from the register": takes a captured record (e.g. a site
// instruction row), lets the user pick a stored client template of the matching
// document type, merges the captured data into it (missing fields → "n/a"),
// previews the result, and generates a PDF (browser print) or downloads the HTML.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";
import { renderTemplate, fieldsFor, templateScope } from "../utils/templateEngine";
import { printDocHtml, downloadHtml } from "../utils/printDoc";

const C = tokens.color;

const s = {
  overlay: { position: "fixed", inset: 0, zIndex: 4100, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal: { background: C.surface, borderRadius: 14, width: "min(960px, 100%)", maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.35)", fontFamily: tokens.font?.body },
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "18px 22px", borderBottom: `1px solid ${C.border}` },
  title: { margin: 0, fontSize: 18, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  bar: { display: "flex", gap: 10, alignItems: "center", padding: "12px 22px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" },
  inp: { padding: "7px 9px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12.5, color: C.ink, fontFamily: "inherit", outline: "none", minWidth: 240 },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  previewWrap: { flex: 1, overflow: "auto", background: "#e9edf2", padding: 18 },
  iframe: { width: "100%", height: "100%", minHeight: 420, border: "none", background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,0.12)" },
};

export default function PopulateTemplateModal({ projectId, project, docType, row, label, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    // Only this client's templates of the right type — never another client's.
    const scope = templateScope(project);
    if (!scope) { setTemplates([]); return undefined; }
    const q = query(collection(db, "templates"), where("scope", "==", scope));
    const unsub = onSnapshot(q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.docType === docType);
        setTemplates(list);
        setSelectedId((cur) => {
          if (cur && list.some((t) => t.id === cur)) return cur;
          if (row?.templateId && list.some((t) => t.id === row.templateId)) return row.templateId;
          return (list[0] && list[0].id) || "";
        });
      },
      (err) => { console.error("templates listener:", err); setTemplates([]); }
    );
    return unsub;
  }, [docType, project, row?.templateId]);

  const selected = templates.find((t) => t.id === selectedId) || null;
  const data = useMemo(() => fieldsFor(docType, row || {}, project || {}), [docType, row, project]);
  const filled = useMemo(() => (selected ? renderTemplate(selected.html, data) : ""), [selected, data]);

  const fileBase = `${(label || docType || "document").replace(/[^\w-]+/g, "_")}`;

  return createPortal(
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.head}>
          <div>
            <h3 style={s.title}>Populate template — {label || docType}</h3>
            <p style={s.sub}>Captured data is merged into the client template; blank fields print as “n/a”.</p>
          </div>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>

        <div style={s.bar}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.inkMuted }}>Template:</span>
          {templates.length === 0 ? (
            <span style={{ ...s.sub, margin: 0 }}>
              No {docType} templates yet — add one in the Templates section.
            </span>
          ) : (
            <select style={s.inp} value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.client ? ` — ${t.client}` : ""}</option>
              ))}
            </select>
          )}
          <div style={{ flex: 1 }} />
          <button style={s.btn} disabled={!selected} onClick={() => downloadHtml(filled, `${fileBase}.html`)}>Download HTML</button>
          <button style={s.btnAccent} disabled={!selected} onClick={() => printDocHtml(filled, fileBase)}>Generate PDF</button>
        </div>

        <div style={s.previewWrap}>
          {selected
            ? <iframe title="preview" style={s.iframe} sandbox="" srcDoc={filled} />
            : <p style={{ ...s.sub, textAlign: "center", marginTop: 40 }}>Select or create a template to preview the populated document.</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}
